import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, BookOpen, User, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ErrorAlert from '../components/ErrorAlert';
import SetNewPassword from '../components/SetNewPassword';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PasskeyLoginButton from '../components/PasskeyLoginButton';
import PasskeyEnrollPrompt from '../components/PasskeyEnrollPrompt';
import { COUNTRIES, detectBrowserCountry } from '../utils/countries';
import CountryCombobox from '../components/CountryCombobox';
import { Zap, Brain } from 'lucide-react';

// One-shot post-login redirect — drops every signed-in user (Reader,
// Writer, Admin alike) into the Speed Reader Challenge on FIRST login
// per browser. The challenge is then dismissible and we set a localStorage
// flag so it never pops up again for the same browser/user combo.
//
// `nav` is react-router's navigate(). `fallback` is where we'd have
// gone normally (eg. '/dashboard').
const SPEED_TEST_SEEN_KEY = 'speed_test_seen_v1';
const redirectAfterLogin = (nav, fallback = '/dashboard') => {
  try {
    if (!localStorage.getItem(SPEED_TEST_SEEN_KEY)) {
      localStorage.setItem(SPEED_TEST_SEEN_KEY, String(Date.now()));
      nav('/speed-test?source=postlogin');
      return;
    }
  } catch { /* SSR / private browsing — fall through */ }
  nav(fallback);
};

const UserLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '', referralCode: '', wantAmbassador: false, utm: '', country: '' });
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [enrollPromptEmail, setEnrollPromptEmail] = useState(null);

  // Auto-detect country from browser locale once on mount — the user can
  // still override it via the dropdown before submitting.
  useEffect(() => {
    const detected = detectBrowserCountry();
    if (detected) setRegisterData((d) => ({ ...d, country: detected }));
  }, []);

  // Jun 3 2026 — prefill the referral-code field + auto-flip the
  // Smarter-Ambassador opt-in when ?ref=... is on the URL. Also flips
  // the visible form to the Register tab so the user lands directly
  // on the signup path with their inviter's code already populated.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const ref = (p.get('ref') || '').trim().toUpperCase();
      // Jun 3 2026 — capture &utm= so signups can be attributed to the
      // channel (author vs ambassador) for admin analytics.
      const utm = (p.get('utm') || '').trim().toLowerCase().slice(0, 32);
      if (ref || utm) {
        setRegisterData((d) => ({
          ...d,
          referralCode: ref || d.referralCode,
          utm: utm || d.utm,
        }));
      }
    } catch { /* SSR / no window */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Enable cookies
        body: JSON.stringify(loginData)
      });

      if (!res.ok) {
        const error = await res.json();
        const errorMessage = error.detail || 'Login failed. Please check your credentials.';
        setLoginError(errorMessage);
        return;
      }

      const data = await res.json();
      // Session token is now in httpOnly cookie - no need to store in localStorage
      localStorage.setItem('userRole', data.role || 'user');
      localStorage.setItem('userEmail', data.email);
      
      // Check if password change is required
      if (data.requires_password_change) {
        setShowPasswordChange(true);
        return;
      }

      // Offer passkey enrolment once per session when this account has none.
      setEnrollPromptEmail(data.email || loginData.email);
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Network error. Please check your connection and try again.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');

    // Client-side validation — surface obvious issues BEFORE the network
    // hop so the user doesn't bounce off a generic "Registration failed"
    // alert (Feb 28 2026 user report — production registration UX).
    const username = (registerData.username || '').trim();
    const email = (registerData.email || '').trim().toLowerCase();
    const password = registerData.password || '';
    if (!username) {
      setRegisterError('Please enter a username so others know who you are.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setRegisterError('Enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      setRegisterError('Password must be at least 8 characters.');
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Enable cookies
        body: JSON.stringify({
          username, email, password, role: 'user',
          country: registerData.country || null,
          // Ambassador opt-in + recruiter number — backend handles both
          // atomically with the user-create. Invalid recruiter codes
          // surface as a soft `recruiter_warning` in the response.
          is_ambassador: !!registerData.wantAmbassador,
          recruiter_ambassador_number: ((registerData.referralCode || '').trim().match(/^\d{1,5}$/) || [''])[0],
        })
      });

      // Robustly parse the body — production occasionally returns HTML
      // 502/504 pages when the backend is restarting and `res.json()`
      // would throw, falling through to the generic catch block. We try
      // text first then attempt JSON to recover the actual `detail` if
      // available.
      const rawText = await res.text();
      let parsed = null;
      try { parsed = rawText ? JSON.parse(rawText) : null; } catch { /* not JSON */ }

      if (!res.ok) {
        // FastAPI sometimes returns a Pydantic validation list under
        // `.detail`. Flatten to a readable string.
        let detail = parsed?.detail;
        if (Array.isArray(detail)) {
          detail = detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
        }
        if (!detail) detail = `Registration failed (HTTP ${res.status}).`;
        // Surface a "log in instead" hint when the email is already on file.
        if (typeof detail === 'string' && /already (registered|exists)/i.test(detail)) {
          setRegisterError(
            `${detail} If this is you, switch to the Login tab — or reset your password from there.`,
          );
        } else {
          setRegisterError(String(detail));
        }
        return;
      }

      const data = parsed || {};
      // Session token is now in httpOnly cookie — no need to store in localStorage
      localStorage.setItem('userRole', 'user');
      localStorage.setItem('userEmail', data.email || email);
      localStorage.setItem('username', data.name || username);
      // Jun 3 2026 spec — Smarter-Ambassador opt-in (asked inline on
      // the register form). Also accepts an optional referral code.
      try {
        if (registerData.wantAmbassador) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/codes/become-ambassador`, {
            method: 'POST', credentials: 'include',
          });
        }
        const code = (registerData.referralCode || '').trim().toUpperCase();
        if (code) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/codes/redeem`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, utm: registerData.utm || '' }),
          });
        }
      } catch { /* non-fatal */ }
      // Offer passkey enrolment immediately after a fresh signup.
      setEnrollPromptEmail(data.email || email);
    } catch (error) {
      console.error('Registration error:', error);
      setRegisterError(
        'Network error — please check your connection. If the problem persists, the server may be restarting; try again in a minute.',
      );
    }
  };

  const handleGoogleLogin = () => {
    // Google Sign-in removed by user request
    alert('Google Sign-in has been disabled. Please use email/password login.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-8">
      {showPasswordChange && (
        <SetNewPassword
          onClose={() => setShowPasswordChange(false)}
          onSuccess={() => {
            setShowPasswordChange(false);
            redirectAfterLogin(navigate);
          }}
        />
      )}

      {enrollPromptEmail && (
        <PasskeyEnrollPrompt
          email={enrollPromptEmail}
          onClose={() => {
            setEnrollPromptEmail(null);
            redirectAfterLogin(navigate);
          }}
        />
      )}
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Reader Login</h1>
          <p className="text-gray-400 text-sm">Access your reading library</p>
        </div>

        {/* Pre-login teaser banner — drops anonymous visitors into the
            Speed Reader Challenge so they sample the gamified UX before
            committing to signup. The XP they "earn" anonymously is
            held back behind a "Sign up to save your XP" CTA at the end
            of the test, turning the test itself into the funnel. */}
        <Link
          to="/speed-test"
          className="group block mb-3 rounded-2xl border border-[#FF3B30]/40 bg-gradient-to-r from-[#FF3B30]/15 to-[#FFD60A]/10 px-4 py-3 hover:border-[#FF3B30] transition"
          data-testid="prelogin-speed-test-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF3B30]/20 grid place-items-center shrink-0 group-hover:bg-[#FF3B30]/30 transition">
              <Zap className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFD60A] font-bold">No signup needed</p>
              <p className="text-sm font-bold text-white">Can you read 900 words a minute?</p>
              <p className="text-[11px] text-white/60">Take the Speed Reader Challenge →</p>
            </div>
          </div>
        </Link>

        {/* Companion Flash-Read + Retention Quiz teaser. Same gating
            pattern: anonymous play, XP only credited post-signup. */}
        <Link
          to="/quiz-demo"
          className="group block mb-4 rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/15 to-fuchsia-500/10 px-4 py-3 hover:border-amber-300 transition"
          data-testid="prelogin-quiz-demo-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 grid place-items-center shrink-0 group-hover:bg-amber-500/30 transition">
              <Brain className="w-5 h-5 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">No signup needed</p>
              <p className="text-sm font-bold text-white">Flash read &amp; remember — how much sticks?</p>
              <p className="text-[11px] text-white/60">Take the Retention Quiz →</p>
            </div>
          </div>
        </Link>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/50">
          <div className="mb-5">
            <GoogleSignInButton role="user" label="Continue with Google" testId="reader-google-signin-btn" />
            <div className="mt-3">
              <PasskeyLoginButton
                defaultEmail={loginData.email}
                onSuccess={(u) => {
                  localStorage.setItem('userRole', u.role || 'user');
                  localStorage.setItem('userEmail', u.email);
                  redirectAfterLogin(navigate);
                }}
                testId="reader-passkey-login-btn"
              />
            </div>
            <div className="flex items-center my-5">
              <div className="flex-1 h-px bg-slate-700" />
              <span className="px-3 text-xs text-gray-500 uppercase tracking-wider">or use email</span>
              <div className="flex-1 h-px bg-slate-700" />
            </div>
          </div>
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <ErrorAlert message={loginError} onClose={() => setLoginError('')} />
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="email" placeholder="Email" value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-slate-700 text-white" required />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type={showPassword ? 'text' : 'password'} placeholder="Password" value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="w-full pl-12 pr-12 py-6 bg-slate-900/50 border-slate-700 text-white" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <Button type="submit" className="w-full py-6 bg-green-600 hover:bg-green-700">Sign In</Button>
              </form>
              
              <div className="mt-4 text-center">
                <a 
                  href="/forgot-password" 
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  Forgot Password?
                </a>
              </div>
            </TabsContent>

            <TabsContent value="register">
              <ErrorAlert message={registerError} onClose={() => setRegisterError('')} />
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="text" placeholder="Username" value={registerData.username}
                    onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                    className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-slate-700 text-white" required />
                </div>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="email" placeholder="Email" value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    className="w-full pl-12 pr-4 py-6 bg-slate-900/50 border-slate-700 text-white" required />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type={showPassword ? 'text' : 'password'} placeholder="Password" value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    className="w-full pl-12 pr-12 py-6 bg-slate-900/50 border-slate-700 text-white" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {/* Country selector — auto-detected from browser locale,
                    user can override. Alphabetical + searchable
                    (Feb 2026 fix). */}
                <CountryCombobox
                  value={registerData.country}
                  onChange={(code) => setRegisterData({ ...registerData, country: code })}
                  placeholder="Country (optional)"
                  testId="register-country-select"
                />
                {/* Optional referral code OR ambassador number. The
                    backend handles both — a 1-5 digit input gets sent
                    to the ambassador-recruiter linker; alphanumeric
                    codes (SY-W-XXXXX) go to the scribe codes endpoint. */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Referral code or Ambassador # (optional)"
                    value={registerData.referralCode}
                    onChange={(e) => setRegisterData({ ...registerData, referralCode: e.target.value.toUpperCase() })}
                    className="w-full pl-4 pr-3 py-6 bg-slate-900/50 border-slate-700 text-white tracking-wider font-mono uppercase placeholder:font-sans placeholder:normal-case placeholder:tracking-normal"
                    data-testid="register-referral-code"
                    maxLength={24}
                  />
                  {/^\d{1,5}$/.test((registerData.referralCode || '').trim()) && (
                    <p className="text-[10px] text-pink-200 mt-1 ml-1" data-testid="register-recruiter-hint">
                      You're crediting Ambassador #{registerData.referralCode.trim().padStart(3, '0')}
                    </p>
                  )}
                </div>
                {/* Smarter-Ambassador opt-in — checkbox now also asks
                    the new ambassador system to mint a tracking number
                    for this account so admins can see who's referring
                    whom. */}
                <label
                  className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/40 border border-slate-700 cursor-pointer hover:border-pink-400/40"
                  data-testid="register-ambassador-prompt"
                >
                  <input
                    type="checkbox"
                    checked={registerData.wantAmbassador}
                    onChange={(e) => setRegisterData({ ...registerData, wantAmbassador: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-pink-500"
                    data-testid="register-ambassador-checkbox"
                  />
                  <div className="text-left">
                    <p className="text-sm font-bold text-pink-200">I'm a marketer / Smarter-Ambassador</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Get your own 3-digit Ambassador number (e.g. #007), earn coins per recruited reader, and wear the badge on your profile.
                    </p>
                  </div>
                </label>
                <Button type="submit" className="w-full py-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium">
                  Create Account
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-6 flex justify-center gap-4 text-sm">
          <Link to="/writer-login" className="text-purple-400 hover:text-purple-300">Scribe Login →</Link>
          <Link to="/admin-login" className="text-red-400 hover:text-red-300">Admin Login →</Link>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;